/**
 * TTS Configuration Service
 * Centralized service for managing TTS configuration across the application
 */

import api from './api';

/**
 * Get the current TTS configuration
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} TTS configuration
 */
export const getTTSConfiguration = async (token) => {
  try {
    const response = await api.get('/tts/configuration', token);
    return response.data?.configuration || null;
  } catch (error) {
    console.error('Failed to fetch TTS configuration:', error);
    return null;
  }
};

/**
 * Get voice settings for a specific provider
 * @param {string} token - Authentication token
 * @param {string} provider - Provider name (elevenlabs, google, azure, openai)
 * @returns {Promise<Object>} Voice settings for the provider
 */
export const getProviderVoiceSettings = async (token, provider = 'elevenlabs') => {
  try {
    const config = await getTTSConfiguration(token);
    if (!config?.platformRoleVoices) return {};
    
    // Extract voice settings for the specified provider
    const voiceSettings = {};
    Object.keys(config.platformRoleVoices).forEach(role => {
      const roleConfig = config.platformRoleVoices[role];
      if (roleConfig && typeof roleConfig === 'object') {
        // Get the voice for this provider, or fallback to primary
        voiceSettings[role] = roleConfig[provider] || roleConfig.primary || roleConfig.fallback;
      }
    });
    
    return voiceSettings;
  } catch (error) {
    console.error('Failed to fetch provider voice settings:', error);
    return {};
  }
};

/**
 * Convert new TTS configuration format to legacy platform settings format
 * This is for backward compatibility with existing components
 * @param {Object} ttsConfig - TTS configuration
 * @param {string} provider - Provider to use for voice IDs
 * @returns {Object} Legacy format voice settings
 */
export const convertToLegacyVoiceSettings = (ttsConfig, provider = 'elevenlabs') => {
  if (!ttsConfig?.platformRoleVoices) return {};
  
  const legacySettings = {
    defaultAITherapistVoiceId: ttsConfig.platformRoleVoices.ai_therapist?.[provider] || 
                               ttsConfig.platformRoleVoices.ai_therapist?.primary ||
                               ttsConfig.platformRoleVoices.ai_therapist?.fallback,
    defaultHumanTherapistVoiceId: ttsConfig.platformRoleVoices.human_therapist?.[provider] ||
                                  ttsConfig.platformRoleVoices.human_therapist?.primary,
    defaultClientVoiceId: ttsConfig.platformRoleVoices.client?.[provider] ||
                         ttsConfig.platformRoleVoices.client?.primary,
    defaultSupervisorVoiceId: ttsConfig.platformRoleVoices.supervisor?.[provider] ||
                             ttsConfig.platformRoleVoices.supervisor?.primary,
    defaultSystemVoiceId: ttsConfig.platformRoleVoices.system?.[provider] ||
                         ttsConfig.platformRoleVoices.system?.primary
  };
  
  return legacySettings;
};

/**
 * Update TTS configuration with new voice settings
 * @param {string} token - Authentication token
 * @param {Object} voiceSettings - Voice settings in legacy format
 * @param {string} provider - Provider to update
 * @returns {Promise<Object>} Updated configuration
 */
export const updateVoiceSettings = async (token, voiceSettings, provider = 'elevenlabs') => {
  try {
    // Get current configuration
    const currentConfig = await getTTSConfiguration(token);
    if (!currentConfig) {
      throw new Error('Could not fetch current TTS configuration');
    }
    
    // Convert legacy format to new format
    const updatedRoleVoices = {
      ...currentConfig.platformRoleVoices
    };
    
    // Map legacy keys to new role keys
    const keyMapping = {
      defaultAITherapistVoiceId: 'ai_therapist',
      defaultHumanTherapistVoiceId: 'human_therapist',
      defaultClientVoiceId: 'client',
      defaultSupervisorVoiceId: 'supervisor',
      defaultSystemVoiceId: 'system'
    };
    
    Object.keys(voiceSettings).forEach(legacyKey => {
      const roleKey = keyMapping[legacyKey];
      if (roleKey && voiceSettings[legacyKey]) {
        if (!updatedRoleVoices[roleKey]) {
          updatedRoleVoices[roleKey] = {};
        }
        updatedRoleVoices[roleKey][provider] = voiceSettings[legacyKey];
      }
    });
    
    // Update configuration
    const updatedConfig = {
      ...currentConfig,
      platformRoleVoices: updatedRoleVoices
    };
    
    const response = await api.put('/tts/configuration', updatedConfig, token);
    return response.data;
  } catch (error) {
    console.error('Failed to update voice settings:', error);
    throw error;
  }
};

/**
 * Get the current interface configuration for a specific interface
 * @param {string} token - Authentication token
 * @param {string} interfaceName - Interface name (client_chat, therapist_review, etc.)
 * @returns {Promise<Object>} Interface configuration
 */
export const getInterfaceConfig = async (token, interfaceName = 'client_chat') => {
  try {
    const config = await getTTSConfiguration(token);
    return config?.interfaceConfigs?.[interfaceName] || null;
  } catch (error) {
    console.error('Failed to fetch interface configuration:', error);
    return null;
  }
};

/**
 * Get the active provider for a specific interface
 * @param {string} token - Authentication token
 * @param {string} interfaceName - Interface name
 * @returns {Promise<string>} Provider name
 */
export const getActiveProviderForInterface = async (token, interfaceName = 'client_chat') => {
  const interfaceConfig = await getInterfaceConfig(token, interfaceName);
  return interfaceConfig?.provider || 'elevenlabs';
};

export default {
  getTTSConfiguration,
  getProviderVoiceSettings,
  convertToLegacyVoiceSettings,
  updateVoiceSettings,
  getInterfaceConfig,
  getActiveProviderForInterface
};